import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardSkeleton } from "web_myplann/Dashboard/Components/DashboardSkeleton";
import {
	getSavedDashboardIndex,
	saveDashboardIndex,
} from "web_myplann/Dashboard/Hooks/useSavedDashboardIndex";
import { getDashboardGradient } from "web_myplann/Dashboard/Utils/getDashboardGradient";
import type { PortfolioReportingOverviewTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CarrouselDots } from "web_myplann/Shared/Components/CarrouselDots";
import { PropertyWidgetTooltip } from "web_myplann/Shared/Components/PropertyWidgetTooltip";
import { useBottomBarSpacing } from "web_myplann/Shared/Hooks/useBottomBarSpacing";
import { useOnScreen } from "web_myplann/Shared/Hooks/useOnScreen";
import {
	hideNativeScrollbarSx,
	useOverlayScrollbar,
} from "web_myplann/Shared/Hooks/useOverlayScrollbar";
import {
	type SwipeableComponentRef,
	useSwipeable,
} from "web_myplann/Shared/Hooks/useSwipeable";
import { DashboardArrowButton } from "../Components/DashboardArrowButton";
import { DashboardCardCarouselResolver } from "../Components/DashboardCardCarouselResolver";
import { DashboardDetailContent } from "../Components/DashboardDetailContent";
import { useDashboardAnimation } from "../Hooks/useDashboardAnimation";
import {
	getDashboardIndexByType,
	useDashboardData,
} from "../Hooks/useDashboardData";
import { useDashboardDetailData } from "../Hooks/useDashboardDetailData";
import { DASHBOARD_HERO_HEIGHT } from "../Utils/dashboardLayout";

const TOOLTIP_OFFSET: [number, number] = [0, -20];

type Props = {
	showArrows?: boolean;
};

export const DashboardDetailPage = ({ showArrows = false }: Props) => {
	const { type } = useParams<{ type: string }>();
	const { t } = useResource();
	const { palette, iconSize } = useTheme();
	const navigate = useNavigate();

	const carrouselRef = useRef<SwipeableComponentRef>(null);
	const heroRef = useRef<HTMLDivElement>(null);
	const scrollerRef = useRef<HTMLDivElement | null>(null);

	useOverlayScrollbar(scrollerRef);
	const bottomBarSpacing = useBottomBarSpacing();

	const {
		dashboards,
		isLoading: isDashboardLoading,
		isEmpty,
	} = useDashboardData();

	const isCardVisible = useOnScreen(heroRef, dashboards.length > 0);

	const urlIndex = getDashboardIndexByType(
		dashboards,
		type as PortfolioReportingOverviewTypeEnum,
	);
	const savedIndex = getSavedDashboardIndex();
	const defaultIndex =
		savedIndex >= 0 && savedIndex < dashboards.length
			? savedIndex
			: Math.max(urlIndex, 0);

	const {
		dashboard,
		activeIndex,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange: onDashboardChange,
		calculateDirection,
	} = useDashboardAnimation(dashboards, defaultIndex, !isEmpty, isCardVisible);

	const handleDashboardChange = (
		newIndex: number,
		direction: "left" | "right" | null,
	) => {
		saveDashboardIndex(newIndex);
		onDashboardChange(newIndex, direction);
	};

	const { portfolios, properties, reporting, isLoading } =
		useDashboardDetailData(dashboard?.overviewType);

	const { handlePointerDown, handlePointerMove, handlePointerEnd } =
		useSwipeable(carrouselRef, false);

	const isSectionLoading = isDashboardLoading || isLoading;

	if (!dashboard) {
		return (
			<Stack minHeight="100dvh" px={1.6} pt={3}>
				<DashboardSkeleton height={180} />
			</Stack>
		);
	}

	return (
		<Stack
			ref={scrollerRef}
			height="100dvh"
			sx={{
				overflowY: "auto",
				overscrollBehavior: "none",
				...hideNativeScrollbarSx,
			}}
		>
			<Stack
				ref={heroRef}
				height={DASHBOARD_HERO_HEIGHT}
				flexShrink={0}
				sx={{
					background: getDashboardGradient(
						dashboard.gradientFrom,
						dashboard.gradientTo,
					),
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					px={1.6}
					mt={1}
				>
					<IconButton
						onClick={() => {
							saveDashboardIndex(
								getDashboardIndexByType(dashboards, dashboard.overviewType),
							);
							navigate(Routing.Dashboard.path());
						}}
						sx={{
							visibility: showArrows ? "hidden" : "visible",
						}}
						aria-hidden={showArrows || undefined}
						tabIndex={showArrows ? -1 : undefined}
					>
						<ArrowLeft size={iconSize.lg} color={palette.darkmodeText.white} />
					</IconButton>
					<Stack direction="row" alignItems="center">
						<Typography
							fontWeight={700}
							fontSize={16}
							color={palette.darkmodeText.white}
						>
							{dashboard.type}
						</Typography>
					</Stack>
					<PropertyWidgetTooltip
						title={t(Resources.Dashboard.TitleCard.tooltip)}
						size={24}
						offset={TOOLTIP_OFFSET}
					/>
				</Stack>

				<Stack
					flex={1}
					alignItems="center"
					justifyContent="center"
					position="relative"
					overflow="hidden"
					sx={{ userSelect: "none", touchAction: "pan-y" }}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerEnd}
					onPointerCancel={handlePointerEnd}
				>
					{isDashboardLoading ? (
						<Box width="100%" pb={3} px={1.6}>
							<DashboardSkeleton height={180} />
						</Box>
					) : (
						<DashboardCardCarouselResolver
							dashboard={dashboard}
							previousDashboard={previousDashboard}
							isAnimating={isAnimating}
							animationDirection={animationDirection}
							isDashboardDetail
						/>
					)}

					{showArrows && dashboards.length > 1 && (
						<>
							<DashboardArrowButton
								direction="left"
								color={palette.darkmodeText.white}
								onClick={() => carrouselRef.current?.swipeLeft()}
							/>
							<DashboardArrowButton
								direction="right"
								color={palette.darkmodeText.white}
								onClick={() => carrouselRef.current?.swipeRight()}
							/>
						</>
					)}

					<Stack
						position="absolute"
						bottom="16px"
						left="50%"
						sx={{ transform: "translateX(-50%)" }}
						zIndex={2}
					>
						<CarrouselDots
							ref={carrouselRef}
							length={dashboards.length}
							currentValue={activeIndex}
							onClick={(val, direction) => {
								const animDirection =
									direction || calculateDirection(dashboard, val);
								handleDashboardChange(val, animDirection);
							}}
						/>
					</Stack>
				</Stack>
			</Stack>

			<Box
				sx={bottomBarSpacing ? { paddingBottom: bottomBarSpacing } : undefined}
			>
				<DashboardDetailContent
					portfolios={portfolios?.data}
					properties={properties?.data}
					reportingData={reporting?.data}
					isLoading={isSectionLoading}
					dashboard={dashboard}
				/>
			</Box>
		</Stack>
	);
};
